<template>
  <div>
    <DatatableReportV3
      v-if="initialData"
      title="Membership Service Utilization"
      download-url="/reports/service_utilization"
      :source="initialData.source"
      :config="config"
      :filters="initialData.filters"
      @filter-applied="onFilterApplied"
      @filter-removed="onFilterRemoved"
      @filters-cleared="onFiltersCleared"
    />
    <AppointmentCreateModal
      v-if="scheduleVisitContext"
      :prefilled_customer="scheduleVisitContext.customer"
      :prefilled_address="scheduleVisitContext.address"
      :membership_program_item_id="
        scheduleVisitContext.membership_program_item_id
      "
      :task_name="scheduleVisitContext.task_name"
      :scheduling_source="scheduleVisitContext.scheduling_source"
      @hide-modal="scheduleVisitContext = null"
      @created="onAppointmentCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { EventBus } from "@/lib/event_bus.js";
import { trackEvent } from "@/lib/pendo";

import Badge from "@/components/Badge.vue";
import { Filter } from "@/components/DatatableV3.vue";
import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import ScheduleVisitButton, {
  type ScheduleVisitEventPayload
} from "@/AppointmentCreateModal/ScheduleVisitButton.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";

interface InitialPageData {
  source: string;
  filters: Filter[];
}

interface FilterAppliedPayload {
  filter_name: string;
  filter_value: unknown;
  filter_value_count?: number;
}

interface FilterRemovedPayload {
  filter_name: string;
}

interface FiltersClearedPayload {
  filters_cleared_count: number;
}

const initialData = ref<InitialPageData | null>(null);
const scheduleVisitContext = ref<ScheduleVisitEventPayload | null>(null);
const router = useRouter();

const onFilterApplied = (payload: FilterAppliedPayload) => {
  trackEvent("service_utilization_filter_applied", payload);
};

const onFilterRemoved = (payload: FilterRemovedPayload) => {
  trackEvent("service_utilization_filter_removed", payload);
};

const onFiltersCleared = (payload: FiltersClearedPayload) => {
  trackEvent("service_utilization_filters_cleared", payload);
};

const handleOpenScheduleVisitModal = (payload: ScheduleVisitEventPayload) => {
  scheduleVisitContext.value = payload;
};

const onAppointmentCreated = (job: { id?: number } | undefined) => {
  scheduleVisitContext.value = null;
  EventBus.$emit("refresh-datatable");
  if (job?.id) {
    router.push(`/jobs/${job.id}`);
  }
};

const config = {
  // Default sort is Available until ascending — shows the most urgent rows
  // first so the admin doesn't have to paginate to triage.
  order: [[1, "asc"]],
  language: {
    emptyTable: "No services available"
  },
  createdRow: (row: HTMLElement, _data: unknown, dataIndex: number) => {
    row.setAttribute("data-cy", `service-utilization-row-${dataIndex}`);
  },
  vueComponents: [
    { rootId: "badge-vue", component: Badge },
    { rootId: "tag-list-vue", component: TagList },
    {
      rootId: "schedule-visit-button-vue",
      component: ScheduleVisitButton
    }
  ],
  columns: [
    { data: "status", title: "Status", searchable: false },
    {
      data: "available_until",
      title: "Available until",
      searchable: false,
      filterName: "ends_at"
    },
    { data: "customer", title: "Customer", aggregateTitle: "# of Customers" },
    {
      data: "customer_tags",
      title: "Customer Tags",
      orderable: false,
      searchable: false,
      filterName: "customer_tags"
    },
    {
      data: "task",
      title: "Task",
      orderable: false,
      searchable: false,
      filterName: "line_item_ids",
      headerClassName: "su-task-filter-column"
    },
    { data: "service_address", title: "Service Address" },
    { data: "zip", title: "Zip", filterName: "zipcode" },
    { data: "completion_date", title: "Completion Date", searchable: false },
    {
      data: "last_service_date",
      title: "Last Service Date",
      orderable: false,
      searchable: false,
      filterName: "last_service_date"
    },
    {
      data: "invoice",
      title: "Invoice",
      orderable: false,
      searchable: false
    },
    { data: "program", title: "Program" },
    {
      data: "equipment",
      title: "Equipment",
      orderable: false,
      searchable: false,
      filterName: "component_types"
    },
    {
      data: "equipment_age",
      title: "Equipment Age",
      orderable: false,
      searchable: false,
      filterName: "age_between"
    },
    { data: "membership_id", title: "Membership ID" },
    {
      data: "covered_systems",
      title: "Covered Systems",
      orderable: false,
      searchable: false,
      filterName: "covered_systems"
    },
    {
      data: "actions",
      title: "",
      orderable: false,
      searchable: false
    }
  ]
};

onMounted(async () => {
  EventBus.$on("open-schedule-visit-modal", handleOpenScheduleVisitModal);
  const res = await fetch(
    `${window.location.origin}/reports/service_utilization/initial_page_data`,
    { method: "GET" }
  );
  initialData.value = await res.json();
});

onUnmounted(() => {
  EventBus.$off("open-schedule-visit-modal", handleOpenScheduleVisitModal);
});
</script>

<style lang="scss">
// The controller sets autoWidth: false on the line_item_filter for this
// report, so the Select fills its column container instead of measuring
// the longest task label against the viewport. This cap keeps the Task
// column from expanding when a verbose task is selected; the pill chip
// styling itself lives on the shared Select component.
th.su-task-filter-column {
  max-width: 260px;
}
</style>
